import { useEffect, useRef } from 'react';
import type { FormEvent, ReactNode } from 'react';
import type { Tag } from '../api';

export function Banner({
  kind = 'info',
  children,
  onDismiss,
}: {
  kind?: 'info' | 'error' | 'success' | 'warn';
  children: ReactNode;
  onDismiss?: () => void;
}) {
  return (
    <div className={`banner banner--${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      <div>{children}</div>
      {onDismiss && (
        <button type="button" className="banner__close" onClick={onDismiss} aria-label="Dismiss">
          ×
        </button>
      )}
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

export function Modal({
  title,
  onClose,
  onSubmit,
  submitLabel = 'Save',
  submitting = false,
  destructive = false,
  children,
}: {
  title: string;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
  submitLabel?: string;
  submitting?: boolean;
  destructive?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Escape closes, and focus moves into the dialog so keyboard users are not
  // left behind the overlay.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>('input, select, textarea, button')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" ref={ref} role="dialog" aria-modal="true" aria-label={title}>
        <header className="modal__head">
          <h2>{title}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <form onSubmit={onSubmit}>
          <div className="modal__body">{children}</div>
          <footer className="modal__foot">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className={`btn ${destructive ? 'btn--danger' : 'btn--primary'}`}
              disabled={submitting}
            >
              {submitting ? 'Working…' : submitLabel}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

/** Editor for the `tags: [{key, value}]` field carried by every schema. */
export function TagEditor({ value, onChange }: { value: Tag[]; onChange: (next: Tag[]) => void }) {
  const update = (i: number, patch: Partial<Tag>) =>
    onChange(value.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));

  return (
    <div className="tag-editor">
      {value.map((tag, i) => (
        <div className="tag-editor__row" key={i}>
          <input
            className="input"
            placeholder="key"
            value={tag.key}
            onChange={(e) => update(i, { key: e.target.value })}
          />
          <input
            className="input"
            placeholder="value"
            value={tag.value}
            onChange={(e) => update(i, { value: e.target.value })}
          />
          <button
            type="button"
            className="icon-btn"
            aria-label="Remove tag"
            onClick={() => onChange(value.filter((_, idx) => idx !== i))}
          >
            ×
          </button>
        </div>
      ))}
      <button type="button" className="btn btn--small" onClick={() => onChange([...value, { key: '', value: '' }])}>
        + Add tag
      </button>
    </div>
  );
}

export function TagList({ tags }: { tags: Tag[] }) {
  if (tags.length === 0) return <span className="muted">—</span>;
  return (
    <span className="tag-list">
      {tags.map((t, i) => (
        <span className="chip" key={i}>
          {t.key}: {t.value}
        </span>
      ))}
    </span>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return <div className="empty">{label}</div>;
}

/**
 * Shown when a temporary password could not be emailed — the only path by
 * which a generated password ever reaches the screen.
 */
export function CredentialCallout({
  email,
  password,
  reason,
}: {
  email: string;
  password: string;
  reason?: string;
}) {
  return (
    <Banner kind="warn">
      <strong>The credentials email could not be delivered.</strong>
      {reason && <div className="muted small">{reason}</div>}
      <div className="cred">
        <div>
          <span className="muted">Email</span>
          <code>{email}</code>
        </div>
        <div>
          <span className="muted">Temporary password</span>
          <code>{password}</code>
        </div>
      </div>
      <div className="small">Copy this now — it is not stored and cannot be shown again.</div>
    </Banner>
  );
}
